import React, { useEffect } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../components/custom/BackButton';
import ActionIcon from '../../../components/custom/ActionIcon';
import { createdUpdatedInfo, getTsDate } from '../../../utils';
import { IUser, Team } from '../../../globals/interfaces';
import useTypedForm from '../../../components/custom/hookForm/useTypedForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { string, date } from 'yup';
import { createSchema, updateSchema } from './teamSchema';
import { CreateOrUpdateTeam } from './SelectTeam';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    user: IUser;
    selectedTeam: Team | undefined;
    updateTeam: boolean;
    handleCreateUpdateTeam: (data: Team) => Promise<void>;
    handleDeleteTeam: () => Promise<void>;
}

export default function ModalManageTeam({
    modal,
    setModal,
    user,
    selectedTeam,
    updateTeam,
    handleCreateUpdateTeam,
    handleDeleteTeam
}: Props) {

    const {
        formState: {
            isValid
        },
        handleSubmit,
        reset,
        trigger,
        FormInput
    } = useTypedForm<Team>({
        resolver: updateTeam ? yupResolver(updateSchema) : yupResolver(createSchema)
    });

    useEffect(() => {
        const now = getTsDate();
        const { s_email, s_unit } = user;
        if (updateTeam && selectedTeam) {
            reset({
                ...selectedTeam,
                modifiedBy: s_email,
                modifiedAt: now
            });
        } else {
            reset({
                name: '',
                unit: s_unit,
                createdBy: s_email,
                createdAt: now,
                modifiedBy: s_email,
                modifiedAt: now
            });
        }
        trigger();
    }, [selectedTeam, updateTeam]);

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{updateTeam ? 'Update' : 'Create'} Group</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormInput 
                    name={'name'}
                    label={'Group Name'}
                />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    {
                        updateTeam && (
                            <Label>
                                {createdUpdatedInfo(selectedTeam)}
                            </Label>
                        )
                    }
                    <FooterButtonsContainer update={updateTeam}>
                        {
                            updateTeam && (
                                <ActionIcon 
                                    type={'delete'} 
                                    onClick={() => handleDeleteTeam()}
                                    disabled={!isValid} 
                                />
                            )
                        }
                        <ActionIcon 
                            type={'save'} 
                            onClick={handleSubmit(handleCreateUpdateTeam)}
                            disabled={!isValid} 
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;